import React, {useEffect} from 'react'
import paths from '../../routes/paths'
import surveysApi from '../../services/api/surveys/surveysApi'
import PupilDashboard from './PupilDashboard'
import WbLoader from '../../components/common/WbLoader'
import usePupilData from '../../hooks/usePupilData'

/**
 * Where a pupil lands after signing in.
 *
 * This used to send a pupil with exactly one survey straight into it:
 *
 *   if (res.length === 1) window.location.href = res[0].url
 *
 * Two things were wrong with that, and both are the same mistake - deciding on a count instead of on
 * what the pupil came to do.
 *
 * A pupil whose one survey was closed or already completed hit "This survey is closed" on
 * insights.welbee.co.uk, a different origin with no way back. There is no route from that page to
 * Safe Space, so a child who followed their access link to talk to somebody was shown a dead end.
 * Nothing in the payload could have prevented it either: getPupilSurveys returns only name, dueDate
 * and url, with no flag saying whether a survey is open or already taken.
 *
 * And Safe Space access emails share PupilAppEmailVerifyUri with survey emails, so both land here.
 * A pupil following a Safe Space link was redirected out of the portal before the dashboard - the
 * only place Safe Space is offered - ever rendered.
 *
 * So the dashboard always renders. It lists the surveys and Safe Space together, which is the one
 * screen that serves both reasons a pupil arrives, and costs a pupil with a live survey a single
 * click on a card that is already there. Restoring the shortcut safely needs the API to say whether
 * a survey is open and untaken; a count cannot answer that.
 */
export default function PupilSurveys() {
  const [loading, setLoading] = React.useState(true)
  const [surveys, setSurveys] = React.useState([])
  const pupil = usePupilData()

  // Depend on the ids, not on the object.
  //
  // usePupilData parses localStorage on every render, so it hands back a new object each time. An
  // effect keyed on that object re-runs on every render, and this one fetches - the dashboard was
  // issuing getPupilSurveys five times on a single load. The ids are primitives and stable, so the
  // fetch happens once per pupil.
  const schoolId = pupil?.schoolId
  const studentId = pupil?.studentId

  useEffect(() => {
    if (!studentId) {
      window.location.href = paths.pupilDashLogin
      return
    }

    let cancelled = false

    surveysApi
      .getPupilSurveys(schoolId, studentId)
      .then(res => {
        if (cancelled) return
        setSurveys(Array.isArray(res) ? res : [])
      })
      .catch(error => {
        // The dashboard still renders. Safe Space does not depend on this call, and a pupil who came
        // to talk to somebody must not be held behind a survey list that failed to load.
        if (cancelled) return
        console.error('Could not load surveys for the pupil dashboard', error)
        setSurveys([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [schoolId, studentId])

  return loading ? <WbLoader /> : <PupilDashboard surveys={surveys} />
}
